import { useState } from 'react';

import {
    ICreativeBlockTranslations,
    IDynamicCreativeItemViewModel,
    IMediaItemViewModel,
} from 'Scripts/Types/Litium';

import { dpr } from 'Utils/dpr';
import tailwindBreakpoints from 'Utils/tailwindBreakpoints';

import CreativeText from 'Components/Creative/CreativeText';
import CreativeTextOnMedia from 'Components/Creative/CreativeTextOnMedia';
import Markup from 'Components/Markup';
import VideoPlayer from 'Components/VideoPlayer';

interface DynamicCreativeItemViewModel
    extends Omit<IDynamicCreativeItemViewModel, 'columnSpan'> {
    columnSpan?: number;
}

interface CreativeItemProps {
    item: DynamicCreativeItemViewModel;
    isGreater: boolean | null;
    mobileFullWidthItem: boolean;
    mobileCenterText: boolean;
    totalColumns: number;
    shouldLazyLoad: boolean;
    translations: ICreativeBlockTranslations;
    reverseBlockOrderMobile?: boolean;
    index: number;
}
const CreativeItem = ({
    item,
    isGreater,
    mobileFullWidthItem,
    mobileCenterText,
    totalColumns,
    shouldLazyLoad,
    translations,
    reverseBlockOrderMobile,
    index,
}: CreativeItemProps) => {
    const hasMedia =
        item.mediaItem.image.src ||
        item.mediaItem.imageMobile.src ||
        item.mediaItem.videoUrl;

    const srcSetSizes = {
        default: Math.round(
            parseInt(tailwindBreakpoints.md) / (mobileFullWidthItem ? 1 : 2)
        ),
        md: Math.round(
            parseInt(tailwindBreakpoints.lg) / (mobileFullWidthItem ? 1 : 2)
        ),
        lg: Math.round(
            (parseInt(tailwindBreakpoints.xl) / totalColumns) *
                (item.columnSpan || 1)
        ),
        xl: Math.round(
            (parseInt(tailwindBreakpoints.xxl) / totalColumns) *
                (item.columnSpan || 1)
        ),
        xxl: Math.round(
            (parseInt(tailwindBreakpoints.xxxl) / totalColumns) *
                (item.columnSpan || 1)
        ),
        xxxl: Math.round(
            (parseInt(tailwindBreakpoints.xxxl) / totalColumns) *
                (item.columnSpan || 1)
        ),
    };
    const getUrl = (mediaItem: IMediaItemViewModel) => {
        if (isGreater !== null) {
            return isGreater ? mediaItem.videoUrl : mediaItem.videoUrlMobile;
        }
        return null;
    };
    const Tag = item.mediaItem.mediaLinkUrl ? 'a' : 'div';
    const [showReadMoreText, setOpenReadMore] = useState(false);

    const getreadMoreButtonMargin = (position: string) => {
        switch (position) {
            case 'left':
                return 'auto auto auto 0';
            case 'right':
                return 'auto 0 auto auto';
            default:
                return '0 auto';
        }
    };

    return (
        <div
            style={
                {
                    '--order': -index,
                } as React.CSSProperties
            }
            className={`relative ${
                mobileFullWidthItem ? 'col-span-full lg:col-span-1' : ''
            } ${!hasMedia ? 'flex h-full flex-col' : ''} ${
                reverseBlockOrderMobile && 'order-[--order] lg:order-none'
            }`}
        >
            <CreativeText
                text={item.textAboveMedia.text}
                centerTextBetweenColumns={
                    item.textAboveMedia.centerTextBetweenColumns
                }
                textWidth={item.textAboveMedia.textWidth}
                isAboveText
                mobileCenterText={mobileCenterText}
            />

            {translations &&
                item.readMoreText?.value &&
                item.readMoreTextPosition === 'above' && (
                    <>
                        <div
                            className={`mb-0 grid w-full overflow-hidden transition-[grid-template-rows,_visibility] duration-layout lg:mx-auto ${
                                showReadMoreText
                                    ? 'visible grid-rows-[1fr]'
                                    : 'invisible grid-rows-[0fr]'
                            } lg:w-[var(--read-more-text-width)]`}
                            style={
                                {
                                    '--read-more-text-width': `${item.readMoreTextWidth}%`,
                                } as React.CSSProperties
                            }
                        >
                            <Markup
                                html={item.readMoreText.value}
                                cssClass="prose-block mb-0 px-4 lg:px-0 overflow-hidden"
                            />
                        </div>
                        <button
                            className="m-[var(--read-more-button-margin)] flex px-4 pb-4 pt-3 font-roboto font-medium lg:px-0"
                            onClick={() => setOpenReadMore(!showReadMoreText)}
                            style={
                                {
                                    '--read-more-button-margin': `${getreadMoreButtonMargin(
                                        item.readMoreButtonAlignment
                                    )}`,
                                } as React.CSSProperties
                            }
                        >
                            {showReadMoreText
                                ? translations.readLessButtonText
                                : item.readMoreButtonText
                                  ? item.readMoreButtonText
                                  : translations.readMoreButtonText}
                        </button>
                    </>
                )}

            <div className={`relative ${hasMedia ? '' : 'h-full'}`}>
                {hasMedia && (
                    <Tag
                        {...(item.mediaItem.mediaLinkUrl && {
                            href: item.mediaItem.mediaLinkUrl,
                            title: item.mediaItem.mediaLinkText,
                        })}
                        className="relative"
                    >
                        {!item.mediaItem.videoUrl ? (
                            <figure className="w-full object-cover">
                                <picture>
                                    <source
                                        width={
                                            item.mediaItem.image.width > 0
                                                ? item.mediaItem.image.width
                                                : ''
                                        }
                                        height={
                                            item.mediaItem.image.height > 0
                                                ? item.mediaItem.image.height
                                                : ''
                                        }
                                        media={`(min-width: ${tailwindBreakpoints.xxxl})`}
                                        srcSet={dpr(
                                            `${item.mediaItem.image.src}?format=webp&width=${srcSetSizes.xxxl}`
                                        )}
                                    />
                                    <source
                                        width={
                                            item.mediaItem.image.width > 0
                                                ? item.mediaItem.image.width
                                                : ''
                                        }
                                        height={
                                            item.mediaItem.image.height > 0
                                                ? item.mediaItem.image.height
                                                : ''
                                        }
                                        media={`(min-width: ${tailwindBreakpoints.xxl})`}
                                        srcSet={dpr(
                                            `${item.mediaItem.image.src}?format=webp&width=${srcSetSizes.xxl}`
                                        )}
                                    />
                                    <source
                                        width={
                                            item.mediaItem.image.width > 0
                                                ? item.mediaItem.image.width
                                                : ''
                                        }
                                        height={
                                            item.mediaItem.image.height > 0
                                                ? item.mediaItem.image.height
                                                : ''
                                        }
                                        media={`(min-width: ${tailwindBreakpoints.xl})`}
                                        srcSet={dpr(
                                            `${item.mediaItem.image.src}?format=webp&width=${srcSetSizes.xl}`
                                        )}
                                    />
                                    <source
                                        width={
                                            item.mediaItem.image.width > 0
                                                ? item.mediaItem.image.width
                                                : ''
                                        }
                                        height={
                                            item.mediaItem.image.height > 0
                                                ? item.mediaItem.image.height
                                                : ''
                                        }
                                        media={`(min-width: ${tailwindBreakpoints.lg})`}
                                        srcSet={dpr(
                                            `${item.mediaItem.image.src}?format=webp&width=${srcSetSizes.lg}`
                                        )}
                                    />
                                    <source
                                        width={
                                            item.mediaItem.imageMobile.width > 0
                                                ? item.mediaItem.imageMobile
                                                      .width
                                                : ''
                                        }
                                        height={
                                            item.mediaItem.imageMobile.height >
                                            0
                                                ? item.mediaItem.imageMobile
                                                      .height
                                                : ''
                                        }
                                        media={`(min-width: ${tailwindBreakpoints.md})`}
                                        srcSet={dpr(
                                            `${item.mediaItem.imageMobile.src}?format=webp&width=${srcSetSizes.md}`
                                        )}
                                    />
                                    <img
                                        className="w-full object-cover"
                                        src={`${item.mediaItem.imageMobile.src}?format=webp&width=${srcSetSizes.default}`}
                                        srcSet={dpr(
                                            `${item.mediaItem.imageMobile.src}?format=webp&width=${srcSetSizes.default}`
                                        )}
                                        alt={item.mediaItem.imageMobile.altText}
                                        loading={
                                            shouldLazyLoad ? 'lazy' : 'eager'
                                        }
                                        width={
                                            item.mediaItem.imageMobile.width > 0
                                                ? item.mediaItem.imageMobile
                                                      .width
                                                : ''
                                        }
                                        height={
                                            item.mediaItem.imageMobile.height >
                                            0
                                                ? item.mediaItem.imageMobile
                                                      .height
                                                : ''
                                        }
                                    />
                                </picture>
                            </figure>
                        ) : (
                            <VideoPlayer
                                url={getUrl(item.mediaItem)}
                                altDescription={item.mediaItem.mediaText}
                                showController={item.mediaItem.showController}
                                muted={item.mediaItem.muted}
                                autoPlay={item.mediaItem.autoPlay}
                                placeholderAlt={item.mediaItem.mediaText}
                                placeholderLarge={item.mediaItem.image}
                                placeholderSmall={item.mediaItem.imageMobile}
                            />
                        )}
                    </Tag>
                )}
                <CreativeTextOnMedia
                    verticalPosition={item.textOnMedia.verticalPosition}
                    horizontalPosition={item.textOnMedia.horizontalPosition}
                    textWidth={
                        item.textOnMedia.textWidth === 0
                            ? 100
                            : item.textOnMedia.textWidth
                    }
                    text={item.textOnMedia.text}
                    hiddenPlaceholder={!hasMedia}
                    readMoreText={item.readMoreText?.value}
                    readLessButtonText={translations?.readLessButtonText}
                    readMoreButtonText={
                        item.readMoreButtonText
                            ? item.readMoreButtonText
                            : translations?.readMoreButtonText
                    }
                    readMoreTextWidth={item.readMoreTextWidth}
                    readMoreButtonAlignment={item.readMoreButtonAlignment}
                />
            </div>
            {item.textUnderMedia.text?.value && (
                <CreativeText
                    text={item.textUnderMedia.text}
                    centerTextBetweenColumns={
                        item.textUnderMedia.centerTextBetweenColumns
                    }
                    textWidth={
                        item.textUnderMedia.textWidth === 0
                            ? 100
                            : item.textUnderMedia.textWidth
                    }
                    isAboveText={false}
                    mobileCenterText={mobileCenterText}
                />
            )}

            {translations &&
                item.readMoreText?.value &&
                item.readMoreTextPosition === 'below' && (
                    <>
                        <div
                            className={`mb-0 grid w-full overflow-hidden transition-[grid-template-rows,_visibility] duration-layout lg:mx-auto ${
                                showReadMoreText
                                    ? 'visible grid-rows-[1fr]'
                                    : 'invisible grid-rows-[0fr]'
                            } lg:w-[var(--read-more-text-width)]`}
                            style={
                                {
                                    '--read-more-text-width':
                                        item.readMoreTextWidth > 0
                                            ? `${item.readMoreTextWidth}%`
                                            : `100%`,
                                } as React.CSSProperties
                            }
                        >
                            <Markup
                                html={item.readMoreText.value}
                                cssClass="prose-block mb-0 px-4 lg:px-0 overflow-hidden"
                            />
                        </div>
                        <button
                            className="m-[var(--read-more-button-margin)] flex px-4 pb-4 pt-3 font-roboto font-medium lg:px-0"
                            onClick={() => setOpenReadMore(!showReadMoreText)}
                            style={
                                {
                                    '--read-more-button-margin': `${getreadMoreButtonMargin(
                                        item.readMoreButtonAlignment
                                    )}`,
                                } as React.CSSProperties
                            }
                        >
                            {showReadMoreText
                                ? translations.readLessButtonText
                                : item.readMoreButtonText
                                  ? item.readMoreButtonText
                                  : translations.readMoreButtonText}
                        </button>
                    </>
                )}
        </div>
    );
};

export default CreativeItem;
